import React, { useState } from "react";
import { getStrategyColor } from "./strategyColors.js";

const timeColors = {
  p10: '#86efac', // muted green (green-300) – very good
  p25: '#60a5fa', // light blue – leaning positive
  p50: '#3b82f6', // blue-500 – clear neutral/target
  p75: '#f87171', // soft red (red-400) – warning
  p90: '#dc2626'  // muted red (red-600) – bad
};

export default function BettingChart({ bets }) {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [chartType, setChartType] = useState('strategy');
  const [timeBreakdown, setTimeBreakdown] = useState('percentiles');
  const [showOptions, setShowOptions] = useState(false);

  const header = (
    <>
      <h3
        className="chart-dropdown-toggle"
        onClick={() => setShowOptions(!showOptions)}
      >
        {chartType === 'strategy' ? 'Bets by Strategy' : 'Time to Bet'}
        <span className={`chart-caret ${showOptions ? 'open' : ''}`}>▼</span>
      </h3>
      {showOptions && (
        <ul className="chart-dropdown-menu">
          <li onClick={() => { setChartType('strategy'); setShowOptions(false); }}>Bets by Strategy</li>
          <li onClick={() => { setChartType('time'); setShowOptions(false); }}>Time to Bet</li>
        </ul>
      )}
      {chartType === 'time' && (
        <div className="chart-subtoggle">
          <button
            type="button"
            className={timeBreakdown === 'percentiles' ? 'is-active' : ''}
            onClick={() => setTimeBreakdown('percentiles')}
          >
            Percentiles
          </button>
          <button
            type="button"
            className={timeBreakdown === 'strategy' ? 'is-active' : ''}
            onClick={() => setTimeBreakdown('strategy')}
          >
            By strategy
          </button>
        </div>
      )}
    </>
  );

  if (!bets || bets.length === 0) {
    return (
      <div className="chart-section">
        {header}
        <div className="no-data">No betting data available</div>
      </div>
    );
  }

  let lines = null;
  let legendData = [];
  let timeAxisTicks = [];
  let countAxisTicks = [];
  let yLabel = '';

  const width = 600;
  const height = 300;
  const margin = { top: 20, right: 120, bottom: 60, left: 70 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const parseDate = (d) => {
    const [y, m, day] = d.split('-');
    return new Date(Number(y), Number(m) - 1, Number(day));
  };

  if (chartType === 'strategy') {
    const strategies = [...new Set(bets.map(b => b.strategy).filter(Boolean))];
    const strategyTotals = {};
    const dailyCounts = {};

    bets.forEach(bet => {
      if (!bet.timestamp || !bet.strategy) return;
      if (!strategyTotals[bet.strategy]) {
        strategyTotals[bet.strategy] = 0;
      }
      strategyTotals[bet.strategy]++;

      const dateObj = new Date(bet.timestamp);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;

      if (!dailyCounts[dateKey]) {
        dailyCounts[dateKey] = {};
      }
      if (!dailyCounts[dateKey][bet.strategy]) {
        dailyCounts[dateKey][bet.strategy] = 0;
      }
      dailyCounts[dateKey][bet.strategy]++;
    });

    const allDates = Object.keys(dailyCounts).sort();
    if (allDates.length === 0) {
      return (
        <div className="chart-section">
          {header}
          <div className="no-data">No valid betting data available</div>
        </div>
      );
    }

    const timeSeriesData = strategies.map(strategy => {
      const strategyData = allDates.map(date => {
        const [y, m, d] = date.split('-');
        return {
          date,
          count: dailyCounts[date][strategy] || 0,
          timestamp: new Date(Number(y), Number(m) - 1, Number(d))
        };
      });

      const total = strategyData.reduce((sum, d) => sum + d.count, 0);

      return {
        strategy,
        color: getStrategyColor(strategy),
        data: strategyData,
        total
      };
    });

    legendData = [...timeSeriesData].sort((a, b) => b.total - a.total);

    const minTime = parseDate(allDates[0]).getTime();
    const maxTime = parseDate(allDates[allDates.length - 1]).getTime();
    const timeRange = maxTime - minTime || 1;

    const allCounts = Object.values(dailyCounts).flatMap(dayCounts => Object.values(dayCounts));
    const maxCount = Math.max(...allCounts, 1);
    const minCount = 0;

    lines = timeSeriesData.map(({ strategy, color, data }) => {
      if (data.length === 0) return null;

      const points = data.map(({ date, count, timestamp }) => {
        const x = margin.left + ((timestamp.getTime() - minTime) / timeRange) * chartWidth;
        const y = margin.top + chartHeight - ((count - minCount) / (maxCount - minCount)) * chartHeight;
        return { x, y, date, count, strategy, color };
      });

      const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

      return (
        <g key={strategy}>
          <path
            d={pathData}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {points.map((point, i) => (
            <circle
              key={i}
              cx={point.x}
              cy={point.y}
              r={4}
              fill={color}
              stroke="#ffffff"
              strokeWidth={1}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredPoint(point)}
              onMouseLeave={() => setHoveredPoint(null)}
            />
          ))}
        </g>
      );
    });

    const tickCount = Math.min(5, allDates.length);
    for (let i = 0; i < tickCount; i++) {
      const ratio = tickCount === 1 ? 0 : i / (tickCount - 1);
      const dateIndex = Math.floor(ratio * (allDates.length - 1));
      const date = allDates[dateIndex];
      const time = parseDate(date).getTime();
      const x = margin.left + ((time - minTime) / timeRange) * chartWidth;
      timeAxisTicks.push({ time, x, date });
    }

    const countTickCount = 5;
    for (let i = 0; i <= countTickCount; i++) {
      const count = minCount + ((maxCount - minCount) * i / countTickCount);
      const y = margin.top + chartHeight - (i / countTickCount) * chartHeight;
      countAxisTicks.push({ count, y });
    }

    yLabel = 'Bet Count';
  } else {
    const timesByDay = {};
    const timesByDayStrategy = {};

    bets.forEach(bet => {
      if (!bet.timestamp || bet.ms_to_bet == null) return;
      const dateObj = new Date(bet.timestamp);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;
      if (!timesByDay[dateKey]) timesByDay[dateKey] = [];
      timesByDay[dateKey].push(bet.ms_to_bet);

      if (bet.strategy) {
        if (!timesByDayStrategy[dateKey]) timesByDayStrategy[dateKey] = {};
        if (!timesByDayStrategy[dateKey][bet.strategy]) {
          timesByDayStrategy[dateKey][bet.strategy] = [];
        }
        timesByDayStrategy[dateKey][bet.strategy].push(bet.ms_to_bet);
      }
    });

    const allDates = Object.keys(timeBreakdown === 'strategy' ? timesByDayStrategy : timesByDay).sort();
    if (allDates.length === 0) {
      return (
        <div className="chart-section">
          {header}
          <div className="no-data">No valid betting data available</div>
        </div>
      );
    }

    const pct = (arr, p) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const idx = (sorted.length - 1) * p;
      const lower = Math.floor(idx);
      const upper = Math.ceil(idx);
      if (lower === upper) return sorted[idx];
      return sorted[lower] + (sorted[upper] - sorted[lower]) * (idx - lower);
    };

    let timeSeriesData = [];

    if (timeBreakdown === 'strategy') {
      const strategies = [...new Set(bets.map(b => b.strategy).filter(Boolean))];
      timeSeriesData = strategies.map(strategy => {
        const data = allDates.map(date => {
          const [y, m, d] = date.split('-');
          return {
            date,
            value: pct((timesByDayStrategy[date] || {})[strategy] || [], 0.5),
            timestamp: new Date(Number(y), Number(m) - 1, Number(d))
          };
        });
        return { strategy, color: getStrategyColor(strategy), data };
      });
    } else {
      const percDefs = [
        { key: 'p90', label: 'p90', p: 0.9 },
        { key: 'p75', label: 'p75', p: 0.75 },
        { key: 'p50', label: 'p50', p: 0.5 },
        { key: 'p25', label: 'p25', p: 0.25 },
        { key: 'p10', label: 'p10', p: 0.1 },
      ];

      timeSeriesData = percDefs.map(({ key, label, p }) => {
        const data = allDates.map(date => {
          const [y, m, d] = date.split('-');
          return {
            date,
            value: pct(timesByDay[date], p),
            timestamp: new Date(Number(y), Number(m) - 1, Number(d))
          };
        });
        return { key, label, color: timeColors[key], data };
      });
    }

    legendData = timeSeriesData;

    const minTime = parseDate(allDates[0]).getTime();
    const maxTime = parseDate(allDates[allDates.length - 1]).getTime();
    const timeRange = maxTime - minTime || 1;

    const allCounts = timeSeriesData.flatMap(s => s.data.map(d => d.value));
    const maxCount = Math.max(...allCounts, 1);
    const minCount = 0;

    lines = timeSeriesData.map(({ key, color, data, label, strategy }) => {
      if (data.length === 0) return null;

      const points = data.map(({ date, value, timestamp }) => {
        const x = margin.left + ((timestamp.getTime() - minTime) / timeRange) * chartWidth;
        const y = margin.top + chartHeight - ((value - minCount) / (maxCount - minCount)) * chartHeight;
        return { x, y, date, value, label, strategy, color };
      });

      const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

      return (
        <g key={strategy || key}>
          <path
            d={pathData}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {points.map((point, i) => (
            <circle
              key={i}
              cx={point.x}
              cy={point.y}
              r={4}
              fill={color}
              stroke="#ffffff"
              strokeWidth={1}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredPoint(point)}
              onMouseLeave={() => setHoveredPoint(null)}
            />
          ))}
        </g>
      );
    });

    const tickCount = Math.min(5, allDates.length);
    for (let i = 0; i < tickCount; i++) {
      const ratio = tickCount === 1 ? 0 : i / (tickCount - 1);
      const dateIndex = Math.floor(ratio * (allDates.length - 1));
      const date = allDates[dateIndex];
      const time = parseDate(date).getTime();
      const x = margin.left + ((time - minTime) / timeRange) * chartWidth;
      timeAxisTicks.push({ time, x, date });
    }

    const countTickCount = 5;
    for (let i = 0; i <= countTickCount; i++) {
      const count = minCount + ((maxCount - minCount) * i / countTickCount);
      const y = margin.top + chartHeight - (i / countTickCount) * chartHeight;
      countAxisTicks.push({ count, y });
    }

    yLabel = 'Milliseconds';
  }

  return (
    <div className="chart-section">
      {header}
      <div className="chart-container" style={{ position: 'relative' }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${width} ${height}`}
          style={{ maxWidth: '100%', maxHeight: height }}
        >
          <rect
            x={margin.left}
            y={margin.top}
            width={chartWidth}
            height={chartHeight}
            fill="transparent"
            stroke="none"
          />

          {countAxisTicks.map((tick, i) => (
            <line
              key={`grid-y-${i}`}
              x1={margin.left}
              y1={tick.y}
              x2={margin.left + chartWidth}
              y2={tick.y}
              stroke="rgba(71, 85, 105, 0.3)"
              strokeWidth={1}
              strokeDasharray={i === 0 ? 'none' : '3,3'}
            />
          ))}

          <line
            x1={margin.left}
            y1={margin.top + chartHeight}
            x2={margin.left + chartWidth}
            y2={margin.top + chartHeight}
            stroke="#64748b"
            strokeWidth={2}
          />
          <line
            x1={margin.left}
            y1={margin.top + chartHeight}
            x2={margin.left}
            y2={margin.top}
            stroke="#64748b"
            strokeWidth={2}
          />

          {lines}

          {timeAxisTicks.map((tick, i) => (
            <text
              key={`time-${i}`}
              x={tick.x}
              y={height - 35}
              textAnchor="middle"
              fontSize={10}
              fill="#94a3b8"
              fontFamily="Inter, sans-serif"
            >
              {new Date(tick.time).toLocaleDateString()}
            </text>
          ))}

          {countAxisTicks.map((tick, i) => (
            <text
              key={`count-${i}`}
              x={margin.left - 10}
              y={tick.y + 4}
              textAnchor="end"
              fontSize={10}
              fill="#94a3b8"
              fontFamily="Inter, sans-serif"
            >
              {Math.round(tick.count)}
            </text>
          ))}

          <text
            x={margin.left + chartWidth / 2}
            y={height - 5}
            textAnchor="middle"
            fontSize={12}
            fill="#cbd5e1"
            fontWeight={500}
            fontFamily="Inter, sans-serif"
          >
            Date
          </text>
          <text
            x={15}
            y={margin.top + chartHeight / 2}
            transform={`rotate(-90 15 ${margin.top + chartHeight / 2})`}
            textAnchor="middle"
            fontSize={12}
            fill="#cbd5e1"
            fontWeight={500}
            fontFamily="Inter, sans-serif"
          >
            {yLabel}
          </text>

          {legendData.map((item, i) => (
            <g key={`legend-${item.key || item.strategy}`}>
              <circle
                cx={width - 100}
                cy={margin.top + 20 + i * 20}
                r={4}
                fill={item.color}
              />
              <text
                x={width - 90}
                y={margin.top + 25 + i * 20}
                fontSize={10}
                fill="#cbd5e1"
                fontFamily="Inter, sans-serif"
              >
                {item.strategy || item.label}
              </text>
            </g>
          ))}
        </svg>

        {hoveredPoint && (
          <div
            className="chart-tooltip"
            style={{
              position: 'absolute',
              left: `${(hoveredPoint.x / width) * 100}%`,
              top: `${(hoveredPoint.y / height) * 100}%`,
              transform: 'translate(-50%, -100%)',
              pointerEvents: 'none',
              backgroundColor: 'rgba(15, 23, 42, 0.95)',
              color: 'white',
              padding: '8px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap'
            }}
          >
            <div>
              <strong>{hoveredPoint.strategy || hoveredPoint.label}</strong>
            </div>
            <div>Date: {hoveredPoint.date}</div>
            <div>
              {chartType === 'strategy'
                ? `Bet Count: ${hoveredPoint.count}`
                : `Time: ${hoveredPoint.value.toFixed(0)}ms`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
